// EntranceAnalysis.jsx — Rich-text edit view (4-field new data)
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { X, ChevronDown, ChevronUp, Download, DoorOpen, Save, Edit3, Eye, EyeOff, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
// New data source (4 fields: zone, remedy, type, impact)
import { ENTRANCE_ANALYSIS_DEFAULT_DATA, REMEDY_TYPE_LABELS, ENTRANCE_SECTION_LABELS, VIRTUAL_REMEDY_LABEL, VIRTUAL_ENTRY_REMEDIES } from './EntranceAnalysisDefaultData';
// Old data kept as backup: import IntranceData from './IntranceData';
import jsPDF from 'jspdf';
import useIsMobile from '../../../hooks/useIsMobile';
import { useLeaveConfirm } from './Configure/components/ConfirmLeaveDialog';
import { pickLang, LANGUAGE_OPTIONS } from './i18n/languageOptions';

// NOTE: was a flat, unscoped key shared across every one of a user's projects
// — scoped per-project below (component-level const shadows this).

/* ── Type badge color (positive / negative / neutral) ────────────── */
const typeBadgeStyle = (remedies) => {
  if (remedies === 'positive') return { background:'#dcfce7', color:'#16a34a', border:'1px solid #86efac' };
  if (remedies === 'negative') return { background:'#fee2e2', color:'#dc2626', border:'1px solid #fca5a5' };
  return                              { background:'#e0f2fe', color:'#0369a1', border:'1px solid #7dd3fc' }; // neutral
};

/* ── Build initial HTML from entrance item (4 fields) ────────────── */
export const buildEntranceHtml = (item, lang = 'en', withVirtual = true) => {
  const name = item.customName || `Entrance ${item.entranceNumber}`;
  const parts = [`<h2>${name}</h2>`];
  const add = (heading, val) => {
    if (val && val !== 'N/A') parts.push(`<h2>${heading}</h2><p>${val}</p>`);
  };
  add(pickLang(ENTRANCE_SECTION_LABELS.zone, lang),       item.zone || item.direction);
  add(pickLang(ENTRANCE_SECTION_LABELS.energyType, lang), pickLang(REMEDY_TYPE_LABELS[item.remedies], lang) || item.remedies);
  add(pickLang(ENTRANCE_SECTION_LABELS.impact, lang),     pickLang(item.impact, lang));
  add(pickLang(ENTRANCE_SECTION_LABELS.remedies, lang),   pickLang(item.block, lang));
  const virtualRemedy = withVirtual && VIRTUAL_ENTRY_REMEDIES[item.zone];
  if (virtualRemedy) add(pickLang(VIRTUAL_REMEDY_LABEL, lang), pickLang(virtualRemedy, lang));
  return parts.join('') || '<p><br></p>';
};

// True when `html` is exactly one of the 8 canonical default-language
// renders for this item (i.e. nothing the user has typed over it yet) —
// used to tell "still default, safe to re-translate on a language switch"
// apart from "user customised this, leave it alone".
export const isStillDefaultEntranceHtml = (item, html) => {
  if (!html) return true;
  return LANGUAGE_OPTIONS.some(({ code }) => buildEntranceHtml(item, code) === html || buildEntranceHtml(item, code, false) === html);
};

// Default report built straight from the saved entrance picks (no Edit mode needed): same entries
// generateReportData makes. Returns [] when no entrance direction has been picked yet.
export const buildDefaultEntranceReport = (selectionsKey, all32Directions = [], rotation = 0) => {
  let sel = {};
  try { sel = JSON.parse(localStorage.getItem(selectionsKey) || '{}') || {}; } catch (e) { sel = {}; }
  const { entranceCount = 1, entranceDirections = {}, entranceNames = {} } = sel;
  const report = [], processed = new Set();
  for (let i = 1; i <= entranceCount; i++) {
    const key = `entrance_${i}`;
    const name = entranceNames[key] || `Entrance ${i}`;
    (entranceDirections[key] || []).forEach(direction => {
      const entryKey = `${key}_${direction}`;
      if (processed.has(entryKey)) return;
      processed.add(entryKey);
      const info = ENTRANCE_ANALYSIS_DEFAULT_DATA.find(e => e.zone === direction);
      const dirData = all32Directions.find(d => d.area === direction);
      if (info) report.push({ id: entryKey, entranceNumber: i, customName: name, direction, angle: dirData?.angle || 0, compassRotation: rotation, adjustedAngle: ((dirData?.angle || 0) + rotation + 360) % 360, zone: info.zone, remedies: info.remedies || '', impact: info.impact || '', block: info.block || '' });
    });
  }
  return report;
};

/* ── Rich Text Editor ────────────────────────────────────────────── */
const RichEditor = React.memo(function RichEditor({ value, onChange, itemId }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = value || '<p><br></p>';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const exec = useCallback((cmd) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, null);
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const formatBlock = useCallback((tag) => {
    editorRef.current?.focus();
    document.execCommand('formatBlock', false, tag);
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const handleInput = useCallback(() => {
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const btn = (extra = {}) => ({
    border: '1px solid #e8eaed', borderRadius: 4, background: '#fff', color: '#3c4043',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: 26, padding: '0 7px', fontSize: '0.74rem', fontFamily: 'inherit', ...extra,
  });

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <div style={{ display:'flex', alignItems:'center', gap:3, padding:'5px 8px', background:'#fafafa', borderBottom:'1px solid #e8eaed', flexWrap:'wrap' }}>
        <select onMouseDown={e => e.preventDefault()} onChange={e => { formatBlock(e.target.value); e.target.value=''; }} defaultValue=""
          style={{ fontSize:'0.71rem', fontWeight:600, border:'1px solid #e8eaed', borderRadius:4, padding:'2px 6px', color:'#3c4043', background:'#fff', cursor:'pointer', outline:'none', fontFamily:'inherit', height:26 }}>
          <option value="" disabled>Format</option>
          <option value="p">Paragraph</option>
          <option value="h2">H2 — Sub Heading</option>
          <option value="h3">H3 — Title</option>
        </select>
        <span style={{ width:1, height:16, background:'#e8eaed', margin:'0 2px' }} />
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('bold');}}      style={btn({fontWeight:800,width:26,padding:0})}>B</button>
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('italic');}}    style={btn({fontStyle:'italic',width:26,padding:0})}>I</button>
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('underline');}} style={btn({textDecoration:'underline',width:26,padding:0})}>U</button>
        <span style={{ width:1, height:16, background:'#e8eaed', margin:'0 2px' }} />
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('insertUnorderedList');}} style={btn()}>• List</button>
        <span style={{ flex:1 }} />
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('removeFormat');}} style={btn({color:'#80868b',fontSize:'0.67rem'})}>Clear</button>
      </div>
      <div ref={editorRef} contentEditable suppressContentEditableWarning onInput={handleInput}
        data-placeholder="Enter content…" className="entr-editor"
        style={{ minHeight:80, padding:'10px 14px', outline:'none', fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", fontSize:'0.84rem', color:'#202124', lineHeight:1.65 }} />
    </div>
  );
});

/* ── Entrance Item Card ───────────────────────────────────────────── */
const EntranceCard = React.memo(function EntranceCard({ item, onHtmlChange, language }) {
  const label = item.customName || `Entrance ${item.entranceNumber}`;
  return (
    <div style={{ background:'#fff', borderRadius:12, border:'1px solid #00A19D', boxShadow:'0 1px 8px rgba(0,161,157,.08)', overflow:'hidden' }}>
      {/* Header — just name + number */}
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#f0fdfc', borderBottom:'1px solid #e2e8f0' }}>
        <div style={{ width:30, height:30, borderRadius:6, background:'#00A19D', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.78rem', fontWeight:800, flexShrink:0 }}>
          {item.entranceNumber}
        </div>
        <div style={{ fontSize:'0.92rem', fontWeight:700, color:'#0f172a' }}>{label}</div>
      </div>
      {/* Editor */}
      <div style={{ padding:'12px 14px 14px' }}>
        {/* Including `language` in itemId forces the editor to re-sync its
            DOM from `value` on a language switch — otherwise it only
            re-reads `value` on mount, so a re-translated default would
            silently never appear in the box the user is looking at. */}
        <RichEditor itemId={`${item.id}-${language}`} value={item.richHtml} onChange={onHtmlChange} />
      </div>
    </div>
  );
});

/* ══════════════════════════════════════════════════════════════════ */
export default function EntranceAnalysis({
  all32Directions = [],
  compassCenter,
  compassRotation: parentCompassRotation = 0,
  findPolygonIntersection,
  polygonPoints = [],
  baseImage,
  isPolygonComplete,
  canvasWidth = 794,
  canvasHeight = 1123,
  onReportGenerated,
  openModal = false,
  onModalClose = null,
  projectId = null,
  projectName = null,
  language = 'en'
}) {
  // Mobile: this modal's header buttons (with text labels), the 50/50
  // selection-view split (selector | canvas), the 8-column direction grid,
  // and the edit-view minmax(520px,...) card grid were all desktop-only —
  // none of it fit a ~360-400px phone viewport. Fixed below via isMobile-
  // gated styles only; desktop keeps every original style untouched.
  const isMobile = useIsMobile();
  const STORAGE_KEY = `entrance_analysis_report_data_${projectId || 'new'}`;
  const STORAGE_KEY_SELECTIONS = `entrance_analysis_selections_${projectId || 'new'}`;
  const A4_WIDTH = 794;
  const A4_HEIGHT = 1123;

  // No local "showModal" mirror of the openModal prop — same race-condition
  // bug fixed earlier in VastuDevtaGuide.jsx / CutExtensionGuide.jsx /
  // SpaceAnalysis.jsx: the close-effect depended on `onModalClose`, an
  // inline arrow function the parent recreates every render, so it kept
  // re-firing and could close the modal again right after opening — click
  // once did nothing. `openModal` is used directly instead.
  const showModal = openModal;
  const handleCloseModal = () => { if (onModalClose) onModalClose(); };

  const [entranceCount, setEntranceCount] = useState(() => {
    try { if (!STORAGE_KEY_SELECTIONS) return 1; const s = localStorage.getItem(STORAGE_KEY_SELECTIONS); return s ? JSON.parse(s).entranceCount || 1 : 1; } catch { return 1; }
  });
  const [entranceDirections, setEntranceDirections] = useState(() => {
    try { if (!STORAGE_KEY_SELECTIONS) return {}; const s = localStorage.getItem(STORAGE_KEY_SELECTIONS); return s ? JSON.parse(s).entranceDirections || {} : {}; } catch { return {}; }
  });
  const [expandedEntrances, setExpandedEntrances] = useState({});
  const [entranceNames, setEntranceNames] = useState(() => {
    try { if (!STORAGE_KEY_SELECTIONS) return {}; const s = localStorage.getItem(STORAGE_KEY_SELECTIONS); return s ? JSON.parse(s).entranceNames || {} : {}; } catch { return {}; }
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [localRotation, setLocalRotation] = useState(parentCompassRotation || 0);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [viewMode, setViewMode] = useState('selection');
  // Same fix as SpaceAnalysis.jsx: the canvas draws at containerSize *
  // zoomLevel, and desktop's 1.5x default deliberately overflows its own
  // container (start zoomed-in, scroll/zoom out from there). On mobile's
  // ~50%-height stacked panel that meant the property was never visible
  // without scrolling first — looked broken. 1x starts fit-to-container;
  // the zoom in/out buttons work identically either way.
  const [zoomLevel, setZoomLevel] = useState(isMobile ? 1 : 1.5);
  // Mobile only: the selector panel (entrance count + expandable direction
  // grid) and the canvas were forced to a fixed 50/50 height split — with
  // an entrance expanded (its 4x8 direction grid open), the selector panel
  // needed more room than that, so its fields got cramped/hard to tap.
  // Lets the user collapse the canvas entirely to give the fields the full
  // screen, per explicit request, instead of a fixed smaller canvas size.
  const [showMobilePlan, setShowMobilePlan] = useState(true);
  const [editableReportData, setEditableReportData] = useState([]);
  const [savedMsg, setSavedMsg] = useState('');

  // Whenever the report language changes, re-generate any item's richHtml
  // that is STILL a default (never hand-edited) into the newly selected
  // language, so switching the toolbar's language dropdown actually
  // switches this predefined content instead of leaving it stuck in
  // whatever language it was first generated in. Anything the user has
  // typed their own words into is left completely untouched. This updates
  // what's shown in the editor here; clicking "Save Report" (as with any
  // other edit) is still what pushes the change through to the export.
  useEffect(() => {
    setEditableReportData(prev => {
      if (prev.length === 0) return prev;
      let changed = false;
      const next = prev.map(item => {
        if (!isStillDefaultEntranceHtml(item, item.richHtml)) return item;
        const translated = buildEntranceHtml(item, language);
        if (translated === item.richHtml) return item;
        changed = true;
        return { ...item, richHtml: translated };
      });
      return changed ? next : prev;
    });
  }, [language]);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const [loadedImage, setLoadedImage] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 400, height: 600 });

  useEffect(() => { const s = localStorage.getItem(STORAGE_KEY); if (s) setIsSaved(true); }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_SELECTIONS, JSON.stringify({ entranceCount, entranceDirections, entranceNames })); } catch {}
  }, [entranceCount, entranceDirections, entranceNames, STORAGE_KEY_SELECTIONS]);

  // Report data is intentionally kept in localStorage across sessions so old reports remain accessible

  useEffect(() => { if (showModal) setLocalRotation(parentCompassRotation || 0); }, [showModal, parentCompassRotation]);

  useEffect(() => {
    if (baseImage) {
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => setLoadedImage(img);
      img.src = baseImage;
    }
  }, [baseImage]);

  useEffect(() => {
    const update = () => { if (containerRef.current) { const w = containerRef.current.clientWidth - 20; const h = containerRef.current.clientHeight - 20; setCanvasSize({ width: w, height: h }); } };
    if (showModal) { setTimeout(update, 100); window.addEventListener('resize', update); return () => window.removeEventListener('resize', update); }
  }, [showModal]);

  useEffect(() => { if (showModal && viewMode === 'selection' && showMobilePlan) drawCanvas(); }, [showModal, viewMode, loadedImage, localRotation, polygonPoints, compassCenter, canvasSize, zoomLevel, showMobilePlan]);

  const entranceTypeDir = all32Directions.map(d => d.area);

  const calculateIntersection = (center, angle) => {
    if (!isPolygonComplete || !polygonPoints || polygonPoints.length < 3) return null;
    const adjustedAngle = angle + localRotation;
    const angleRad = (adjustedAngle * Math.PI) / 180;
    const rayDir = { x: Math.cos(angleRad), y: Math.sin(angleRad) };
    let nearestIntersection = null, minDistance = Infinity;
    for (let i = 0; i < polygonPoints.length; i++) {
      const j = (i + 1) % polygonPoints.length;
      const p1 = polygonPoints[i], p2 = polygonPoints[j];
      const s = { x: p2.x - p1.x, y: p2.y - p1.y };
      const denom = rayDir.x * s.y - rayDir.y * s.x;
      if (Math.abs(denom) < 1e-10) continue;
      const dx = p1.x - center.x, dy = p1.y - center.y;
      const t = (dx * s.y - dy * s.x) / denom;
      const u = (dx * rayDir.y - dy * rayDir.x) / denom;
      if (t >= 0 && u >= -0.001 && u <= 1.001) {
        const intersection = { x: center.x + t * rayDir.x, y: center.y + t * rayDir.y, distance: t };
        if (intersection.distance < minDistance && intersection.distance > 0.1) { minDistance = intersection.distance; nearestIntersection = intersection; }
      }
    }
    return nearestIntersection;
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const displayWidth = canvasSize.width * zoomLevel, displayHeight = canvasSize.height * zoomLevel;
    canvas.width = displayWidth; canvas.height = displayHeight;
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, displayWidth, displayHeight);
    const scaleX = displayWidth / canvasWidth, scaleY = displayHeight / canvasHeight;
    const mainScale = Math.min(scaleX, scaleY);
    const offsetX = (displayWidth - canvasWidth * mainScale) / 2, offsetY = (displayHeight - canvasHeight * mainScale) / 2;
    const transformPoint = (p) => ({ x: p.x * mainScale + offsetX, y: p.y * mainScale + offsetY });
    if (loadedImage) {
      // Same padding as FloorPlanImage.js in the editor so polygon aligns with the image
      const pad = { left: 75, right: 75, top: 105, bottom: 105 };
      const availW = canvasWidth  - pad.left - pad.right;
      const availH = canvasHeight - pad.top  - pad.bottom;
      const imgAspect   = loadedImage.width / loadedImage.height;
      const availAspect = availW / availH;
      let imgW, imgH, imgX, imgY;
      if (imgAspect > availAspect) {
        imgW = availW; imgH = imgW / imgAspect;
        imgX = pad.left; imgY = pad.top + (availH - imgH) / 2;
      } else {
        imgH = availH; imgW = imgH * imgAspect;
        imgX = pad.left + (availW - imgW) / 2; imgY = pad.top;
      }
      ctx.drawImage(loadedImage, imgX * mainScale + offsetX, imgY * mainScale + offsetY, imgW * mainScale, imgH * mainScale);
    }
    if (isPolygonComplete && polygonPoints && polygonPoints.length >= 3) {
      ctx.save(); ctx.beginPath();
      const fp = transformPoint(polygonPoints[0]); ctx.moveTo(fp.x, fp.y);
      for (let i = 1; i < polygonPoints.length; i++) { const p = transformPoint(polygonPoints[i]); ctx.lineTo(p.x, p.y); }
      ctx.closePath(); ctx.strokeStyle = '#000000'; ctx.lineWidth = 2; ctx.stroke(); ctx.restore();
    }
    if (compassCenter && isPolygonComplete && polygonPoints.length >= 3 && all32Directions.length > 0) {
      const tc = transformPoint(compassCenter);
      all32Directions.forEach(dir => {
        const intersection = calculateIntersection(compassCenter, dir.angle); if (!intersection) return;
        const te = transformPoint(intersection);
        ctx.beginPath(); ctx.moveTo(tc.x, tc.y); ctx.lineTo(te.x, te.y);
        ctx.strokeStyle = '#000000'; ctx.lineWidth = 1; ctx.globalAlpha = 0.6; ctx.stroke(); ctx.globalAlpha = 1;
      });
      all32Directions.forEach(dir => {
        const labelAngle = dir.angle + localRotation + 5.625;
        const li = calculateIntersection(compassCenter, labelAngle - localRotation); if (!li) return;
        const tli = transformPoint(li);
        const dx = tli.x - tc.x, dy = tli.y - tc.y, dist = Math.sqrt(dx*dx+dy*dy);
        if (dist > 5) {
          const lx = tc.x + (dx/dist)*(dist+20), ly = tc.y + (dy/dist)*(dist+20);
          ctx.save();
          ctx.fillStyle = 'rgba(255,255,255,0.9)'; ctx.beginPath(); ctx.arc(lx, ly, 12, 0, Math.PI*2); ctx.fill();
          ctx.font = 'bold 9px Arial'; ctx.fillStyle = '#00857E'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText(dir.area, lx, ly);
          ctx.restore();
        }
      });
      ctx.beginPath(); ctx.arc(tc.x, tc.y, 5, 0, Math.PI*2); ctx.fillStyle = '#00857E'; ctx.fill();
      ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 2; ctx.stroke();
    }
  };

  const handleZoomIn  = () => setZoomLevel(p => Math.min(p + 0.25, 3));
  const handleZoomOut = () => setZoomLevel(p => Math.max(p - 0.25, 0.5));
  const handleResetZoom = () => setZoomLevel(1);

  const getEntranceInfo = (direction) => ENTRANCE_ANALYSIS_DEFAULT_DATA.find(e => e.zone === direction);

  const handleEntranceCountChange = (delta) => {
    const newCount = Math.max(1, Math.min(20, entranceCount + delta));
    if (newCount < entranceCount) {
      const nd = { ...entranceDirections };
      for (let i = newCount + 1; i <= entranceCount; i++) delete nd[`entrance_${i}`];
      setEntranceDirections(nd);
    }
    setEntranceCount(newCount);
  };

  const handleDirectionToggle = (entranceKey, direction) => {
    setEntranceDirections(prev => {
      const current = prev[entranceKey] || [];
      return { ...prev, [entranceKey]: current.includes(direction) ? current.filter(d => d !== direction) : [...current, direction] };
    });
  };

  const toggleExpand = (key) => setExpandedEntrances(prev => ({ ...prev, [key]: !prev[key] }));
  const handleEntranceNameChange = (key, name) => setEntranceNames(prev => ({ ...prev, [key]: name }));
  const getEntranceName = (key, def) => entranceNames[key] || def;
  const hasSelectedDirections = () => Object.values(entranceDirections).some(dirs => dirs && dirs.length > 0);

  const generateReportData = useCallback(() => {
    const report = []; const processed = new Set();
    for (let i = 1; i <= entranceCount; i++) {
      const key = `entrance_${i}`;
      const dirs = entranceDirections[key] || [];
      const name = getEntranceName(key, `Entrance ${i}`);
      dirs.forEach(direction => {
        const entryKey = `${key}_${direction}`; if (processed.has(entryKey)) return; processed.add(entryKey);
        const info = getEntranceInfo(direction);
        const dirData = all32Directions.find(d => d.area === direction);
        if (info) report.push({ id: entryKey, entranceNumber: i, customName: name, direction, angle: dirData?.angle || 0, compassRotation: localRotation, adjustedAngle: ((dirData?.angle || 0) + localRotation + 360) % 360, zone: info.zone, remedies: info.remedies || '', impact: info.impact || '', block: info.block || '' });
      });
    }
    return report;
  }, [entranceCount, entranceDirections, entranceNames, localRotation, all32Directions]);

  const saveReportData = useCallback(() => {
    const report = generateReportData();
    if (report.length === 0) { alert('Please select at least one entrance direction before saving.'); return; }
    setIsSaving(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(report));
      localStorage.setItem(STORAGE_KEY_SELECTIONS, JSON.stringify({ entranceCount, entranceDirections, entranceNames }));
      setIsSaved(true); setTimeout(() => setIsSaving(false), 500);
    } catch (e) { alert('Error saving: ' + e.message); setIsSaving(false); }
  }, [generateReportData]);

  const handleHtml = useCallback((id, html) => {
    setEditableReportData(prev => prev.map(d => d.id === id ? { ...d, richHtml: html } : d));
  }, []);

  const switchToManageMode = () => {
    const report = generateReportData();
    if (report.length === 0) { alert('Please select at least one entrance direction.'); return; }
    setEditableReportData(report.map(item => ({ ...item, richHtml: buildEntranceHtml(item, language) })));
    setViewMode('edit');
  };

  /* ── PDF helpers ── */
  const parseEntrSections = (html) => {
    if (!html) return [];
    const sections = [];
    const parts = html.split(/<h2[^>]*>/i);
    parts.forEach(part => {
      if (!part.trim()) return;
      const hm = part.match(/^(.*?)<\/h2>/i);
      if (hm) {
        const heading = hm[1].replace(/<[^>]+>/g, '').trim();
        const rest = part.replace(/^.*?<\/h2>/i, '');
        const text = rest.replace(/<br\s*\/?>/gi,' ').replace(/<\/p>/gi,' ').replace(/<[^>]+>/g,'').replace(/&amp;/g,'&').replace(/&nbsp;/g,' ').replace(/\s{2,}/g,' ').trim();
        if (heading || text) sections.push({ heading, text });
      } else {
        const text = part.replace(/<[^>]+>/g,'').replace(/&nbsp;/g,' ').replace(/\s{2,}/g,' ').trim();
        if (text) sections.push({ heading: '', text });
      }
    });
    return sections;
  };

  const downloadReportAsPDF = useCallback(async (reportData) => {
    const data = reportData || editableReportData;
    if (data.length === 0) { alert('No report data available'); return; }
    setIsGenerating(true);
    try {
      const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      const pgW = doc.internal.pageSize.getWidth();
      const pgH = doc.internal.pageSize.getHeight();
      // PDF type scale — Title/Subtitle/Description = 25/20/16pt everywhere
      // in this export. Title keeps this report's own (black) theme;
      // Subtitle and Description are ALWAYS black.
      const TYPE_TITLE_SIZE = 25, TYPE_SUBTITLE_SIZE = 20, TYPE_DESCRIPTION_SIZE = 16, TYPE_BLACK = [0, 0, 0];

      const CX = 12, CW = pgW - 24;
      const LINE_H = 8, PAGE_TOP = 41, PAGE_BOTTOM = pgH - 22;
      const ITEM_GAP = 8;
      const _date = new Date().toLocaleDateString('en-US', { month:'long', day:'numeric', year:'numeric' });

      const getUserPoweredBy = () => {
        try {
          const u = JSON.parse(localStorage.getItem('user_data') || 'null');
          return (u && u.website_url && u.website_url.trim()) ? u.website_url.trim() : 'Powered by VastuAnalyzer.com';
        } catch { return 'Powered by VastuAnalyzer.com'; }
      };

      // ── Hindi/Devanagari-safe text rendering ──────────────────────────
      // jsPDF's built-in fonts have no Devanagari glyphs and no shaping engine
      // (matras/conjuncts render out of order), so any line containing
      // Devanagari characters is rasterised on an offscreen canvas — using the
      // browser's own text shaping — and embedded as an image. Pure-Latin text
      // still goes through doc.text() as before (crisp vector, no size cost).
      // BUG FIX: only matched Devanagari (Hindi/Marathi) — Gujarati/Bengali/
      // Tamil/Kannada/Telugu text failed this test and fell through to
      // jsPDF's Latin-only font, printing as garbled symbols. Broadened to
      // cover all 6 non-Latin scripts this app supports a language for
      // (same fix applied to the shared renderer in Configure/index.js).
      const DEVANAGARI_RE = /[ऀ-ॿ઀-૿ঀ-৿஀-௿ఀ-౿ಀ-೿]/;
      const PX_PER_MM = 96 / 25.4;
      const measureCtx = document.createElement('canvas').getContext('2d');
      const hindiFontCss = (fontSizePt, fontStyle) => {
        const isBold   = fontStyle === 'bold' || fontStyle === 'bolditalic';
        const isItalic = fontStyle === 'italic' || fontStyle === 'bolditalic';
        return `${isItalic ? 'italic ' : ''}${isBold ? '700' : '400'} ${(fontSizePt * 96 / 72).toFixed(2)}px "Noto Sans", "Noto Sans Devanagari", "Noto Sans Gujarati", "Noto Sans Bengali", "Noto Sans Tamil", "Noto Sans Kannada", "Noto Sans Telugu", "Nirmala UI", Arial, sans-serif`;
      };
      const wrapMixedText = (text, fontSizePt, fontStyle, maxWidthMM) => {
        measureCtx.font = hindiFontCss(fontSizePt, fontStyle);
        const maxWidthPx = maxWidthMM * PX_PER_MM;
        const words = text.split(' ');
        const lines = [];
        let cur = '';
        words.forEach(word => {
          const test = cur ? cur + ' ' + word : word;
          if (cur && measureCtx.measureText(test).width > maxWidthPx) { lines.push(cur); cur = word; } else { cur = test; }
        });
        if (cur) lines.push(cur);
        return lines;
      };
      const wrapAny = (text, fontSizePt, fontStyle, maxWidthMM) => {
        if (DEVANAGARI_RE.test(text)) return wrapMixedText(text, fontSizePt, fontStyle, maxWidthMM);
        doc.setFont('helvetica', fontStyle || 'normal');
        doc.setFontSize(fontSizePt);
        return doc.splitTextToSize(text, maxWidthMM);
      };
      const drawLineSmart = (line, x, yBaseline, fontSizePt, fontStyle, rgb, align) => {
        if (!DEVANAGARI_RE.test(line)) {
          doc.setFont('helvetica', fontStyle || 'normal');
          doc.setFontSize(fontSizePt);
          doc.setTextColor(...rgb);
          doc.text(line, x, yBaseline, align === 'center' ? { align: 'center' } : undefined);
          return;
        }
        const fontCss = hindiFontCss(fontSizePt, fontStyle);
        measureCtx.font = fontCss;
        const scale = 3;
        const widthPx  = Math.ceil(measureCtx.measureText(line).width) + 4;
        const heightPx = Math.ceil(fontSizePt * 96 / 72 * 1.4);
        const canvas = document.createElement('canvas');
        canvas.width = widthPx * scale;
        canvas.height = heightPx * scale;
        const ctx = canvas.getContext('2d');
        ctx.scale(scale, scale);
        ctx.font = fontCss;
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = `rgb(${rgb.join(',')})`;
        ctx.fillText(line, 2, heightPx * 0.75);
        const widthMM  = widthPx  / PX_PER_MM;
        const heightMM = heightPx / PX_PER_MM;
        if (align === 'center') x -= widthMM / 2;
        doc.addImage(canvas.toDataURL('image/png'), 'PNG', x, yBaseline - heightMM * 0.75, widthMM, heightMM, undefined, 'FAST');
      };

      const addFrame = () => {
        doc.setTextColor(100,100,100); doc.setFontSize(7); doc.setFont('helvetica','normal');
        doc.text(getUserPoweredBy(), 8, 5);
        doc.text(_date, pgW - 8, 5, { align:'right' });
        doc.setDrawColor(180,180,180); doc.setLineWidth(0.35);
        doc.rect(8, 8, pgW - 16, pgH - 16, 'S');
        // Title — Title tier, this report's own (black) theme.
        doc.setTextColor(0,0,0); doc.setFontSize(TYPE_TITLE_SIZE); doc.setFont('helvetica','bold');
        const titleY = 20;
        doc.text('Entrance Analysis', pgW / 2, titleY, { align:'center' });
        // Subtitle — Subtitle tier, always black.
        const subtitleY = titleY + 9;
        doc.setFontSize(TYPE_SUBTITLE_SIZE); doc.setFont('helvetica','bold'); doc.setTextColor(...TYPE_BLACK);
        doc.text('Entrance Analysis  |  Direction-wise Report', pgW/2, subtitleY, { align:'center' });
        const dividerY = subtitleY + 8;
        doc.setDrawColor(200,200,200); doc.setLineWidth(0.2);
        doc.line(10, dividerY, pgW - 10, dividerY);
      };

      let curY = 0, pageNum = 1, isFirst = true, lastEntrance = null;

      const newPage = () => {
        if (!isFirst) doc.addPage();
        isFirst = false; addFrame();
        return PAGE_TOP;
      };

      for (const item of data) {
        const sections = parseEntrSections(item.richHtml || buildEntranceHtml(item, language));
        let sectH = 0;
        sections.forEach(sec => {
          if (sec.heading) sectH += 8;
          if (sec.text) sectH += wrapAny(sec.text, TYPE_DESCRIPTION_SIZE, 'normal', CW - 4).length * LINE_H + 3;
        });
        const showEntr = item.customName !== lastEntrance;
        const itemH = (showEntr ? 14 : 0) + 14 + sectH;

        if (curY === 0) {
          curY = newPage();
        } else if (curY + ITEM_GAP + itemH > PAGE_BOTTOM) {
          doc.setTextColor(100,100,100); doc.setFontSize(7); doc.setFont('helvetica','normal');
          doc.text(`Page ${pageNum}`, pgW - 8, pgH - 4, { align:'right' });
          pageNum++; curY = newPage();
        } else {
          doc.setDrawColor(220,224,228); doc.setLineWidth(0.25);
          doc.line(CX + 20, curY + ITEM_GAP/2, CX + CW - 20, curY + ITEM_GAP/2);
          curY += ITEM_GAP;
        }

        // Entrance name heading (when changes) — Description tier, bold, black.
        if (item.customName !== lastEntrance) {
          drawLineSmart(item.customName || `Entrance ${item.entranceNumber}`, CX + 2, curY + 7, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
          curY += 14; lastEntrance = item.customName;
        }

        // Direction sub-heading — Description tier, normal, black.
        doc.setTextColor(...TYPE_BLACK); doc.setFontSize(TYPE_DESCRIPTION_SIZE); doc.setFont('helvetica','normal');
        doc.text(`Direction: ${item.direction}  |  ${Math.round(item.adjustedAngle || 0)}°`, CX + 2, curY + 7);
        curY += 14;

        // Sections — Description tier throughout, always black.
        sections.forEach(sec => {
          if (sec.heading) {
            drawLineSmart(sec.heading, CX + 2, curY + 6, TYPE_DESCRIPTION_SIZE, 'bold', TYPE_BLACK);
            curY += 8;
          }
          if (sec.text) {
            const lines = wrapAny(sec.text, TYPE_DESCRIPTION_SIZE, 'normal', CW - 4);
            lines.forEach((ln, i) => drawLineSmart(ln, CX + 2, curY + 6 + i * LINE_H, TYPE_DESCRIPTION_SIZE, 'normal', TYPE_BLACK));
            curY += lines.length * LINE_H + 3;
          }
        });
      }

      if (curY > 0) {
        doc.setTextColor(100,100,100); doc.setFontSize(7); doc.setFont('helvetica','normal');
        doc.text(`Page ${pageNum}`, pgW - 8, pgH - 4, { align:'right' });
      }

      const _name = (projectName || 'Entrance_Analysis').replace(/[^a-zA-Z0-9_ -]/g,'').trim().replace(/\s+/g,'_');
      doc.save(`${_name}_Entrance_Analysis.pdf`);
    } catch (e) { alert('Error generating PDF: ' + e.message); }
    finally { setIsGenerating(false); }
  }, [editableReportData, projectName]);

  const quickDownloadPDF = useCallback(() => {
    const report = generateReportData();
    if (report.length === 0) { alert('Please select at least one entrance direction first'); return; }
    const withHtml = report.map(item => ({ ...item, richHtml: buildEntranceHtml(item, language) }));
    setEditableReportData(withHtml);
    setTimeout(() => downloadReportAsPDF(withHtml), 100);
  }, [generateReportData, downloadReportAsPDF]);

  const captureAndSendReport = useCallback(() => {
    if (editableReportData.length === 0) { alert('No report data available'); return; }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(editableReportData));
      if (onReportGenerated) onReportGenerated({ layerId:'entrance-analysis-report', reportData: editableReportData, imageData: null, timestamp: new Date().toISOString() });
      setSavedMsg('Report saved!'); setTimeout(() => setSavedMsg(''), 3000);
    } catch (e) { alert('Error saving: ' + e.message); }
  }, [editableReportData, onReportGenerated]);

  /* ── Group by entrance for edit view ── */
  const groupedItems = useMemo(() => {
    const groups = [];
    editableReportData.forEach(item => {
      const last = groups[groups.length - 1];
      if (last && last.entranceName === item.customName) last.items.push(item);
      else groups.push({ entranceName: item.customName || `Entrance ${item.entranceNumber}`, items: [item] });
    });
    return groups;
  }, [editableReportData]);

  const has32DirectionData = all32Directions && all32Directions.length > 0;

  const leave = useLeaveConfirm({
    onSave: () => { if (viewMode === 'edit') captureAndSendReport(); else saveReportData(); handleCloseModal(); },
    onLeave: handleCloseModal,
  });

  return (
    <div style={{ padding: '0px' }}>
      {leave.dialog}
      <div style={{ display: 'flex' }} />

      {/* Tapping "Entrance Analysis" before Boundary + Compass are set up
          used to render literally nothing — has32DirectionData silently
          gated the whole modal below with no feedback at all. On desktop
          this never showed up because the numbered stage list is worked
          top-to-bottom, but the mobile bottom toolbar lists every stage
          flat with no enforced order, so tapping this one first (or before
          calibration is finished) looked exactly like a dead/broken
          button. Show an explanation instead of nothing. */}
      {showModal && !has32DirectionData && (
        <div style={{ position:'fixed', top:0, left:0, width:'100vw', height:'100vh', background:'rgba(0,0,0,0.88)', zIndex:10500, display:'flex', alignItems:'center', justifyContent:'center', padding:'24px' }}>
          <div style={{ maxWidth:'340px', textAlign:'center' }}>
            <h2 style={{ color:'#fff', fontSize:'16px', fontWeight:700, margin:'0 0 10px' }}>Complete earlier steps first</h2>
            <p style={{ color:'#cbd5e1', fontSize:'13px', lineHeight:1.6, margin:'0 0 22px' }}>
              Entrance Analysis needs the property boundary and compass calibration set up first. Go back and finish "Set Pin / Boundary" and "Apply Compass", then open this again.
            </p>
            <button onClick={handleCloseModal} style={{ background:'#00A19D', color:'#fff', border:'none', borderRadius:'6px', padding:'10px 28px', fontWeight:600, fontSize:'13px', cursor:'pointer' }}>Got it</button>
          </div>
        </div>
      )}

      {showModal && has32DirectionData && (
        /* z-index bumped above the mobile bottom toolbar (10100) and
           sheets (10200+) — this full-screen modal used to render behind
           them on phone widths when opened from the footer nav, making it
           look like it never opened. */
        <div style={{ position:'fixed', top:0, left:0, width:'100vw', height:'100vh', background:'#000', zIndex:10500, display:'flex', flexDirection:'column', overflow:'hidden' }}>

          <style>{`
            .entr-editor { text-align:left; }
            .entr-editor h2:first-child { font-size:1rem; font-weight:800; color:#0f172a; margin:0 0 10px; border-bottom:2px solid #000000; padding-bottom:5px; }
            .entr-editor h2 { font-size:0.82rem; font-weight:700; color:#1e293b; margin:10px 0 2px; text-align:left; }
            .entr-editor h3 { font-size:0.8rem;  font-weight:600; color:#334155; margin:6px 0 1px; text-align:left; }
            .entr-editor p  { font-size:0.82rem; color:#475569; margin:1px 0 6px; line-height:1.65; text-align:left; }
            .entr-editor ul { font-size:0.82rem; color:#475569; padding-left:1.8em; margin:2px 0 6px; line-height:1.65; }
            .entr-editor li { margin-bottom:2px; text-align:left; }
            .entr-editor strong { font-weight:700; color:#1e293b; }
            .entr-editor em { font-style:italic; }
            .entr-editor[data-placeholder]:empty::before { content:attr(data-placeholder); color:#cbd5e1; pointer-events:none; }
            .entr-editor:focus { outline:none; }
            @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
            .spinner { width:12px;height:12px;border:2px solid #fff;border-top-color:transparent;border-radius:50%;animation:spin 1s linear infinite;display:inline-block; }
            .entr-scroll::-webkit-scrollbar{width:8px}.entr-scroll::-webkit-scrollbar-track{background:#f1f5f9}.entr-scroll::-webkit-scrollbar-thumb{background:#00A19D;border-radius:4px}
            .entrance-scroll-panel::-webkit-scrollbar{width:12px}.entrance-scroll-panel::-webkit-scrollbar-track{background:#C7F2F1;border-left:1px solid #333}.entrance-scroll-panel::-webkit-scrollbar-thumb{background:#00857E;border-radius:6px;border:2px solid #C7F2F1}
            .canvas-scroll-container::-webkit-scrollbar{width:12px;height:12px}.canvas-scroll-container::-webkit-scrollbar-track{background:#c7f2f1}.canvas-scroll-container::-webkit-scrollbar-thumb{background:#00857E;border-radius:6px;border:2px solid #c7f2f1}.canvas-scroll-container::-webkit-scrollbar-corner{background:#c7f2f1}
          `}</style>

          {/* ── Header ── */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding: isMobile ? '8px 10px' : '8px 16px', background:'#00A19D', borderBottom:'1px solid #ffffff', minHeight:'56px', gap: isMobile ? '8px' : '16px' }}>
            <div style={{ minWidth: isMobile ? 'auto' : '180px' }}>
              <h2 style={{ color:'#ffffff', margin:0, fontSize: isMobile ? '13px' : '14px', fontWeight:600 }}>
                Entrance Analysis {viewMode === 'edit' && (isMobile ? '(Edit)' : '— Edit Report')}
              </h2>
            </div>
            <div style={{ display:'flex', gap: isMobile ? '6px' : '8px', flexWrap:'wrap', alignItems:'center' }}>
              {viewMode === 'selection' ? (
                <>
                  <button onClick={leave.requestClose} style={{ background:'#dc3545', color:'#fff', padding: isMobile ? '8px 10px' : '8px 12px', border:'none', borderRadius:'4px', cursor:'pointer' }}><X size={16} /></button>
                  <button onClick={switchToManageMode} disabled={!hasSelectedDirections()} style={{ background: hasSelectedDirections()?'#00857E':'#555', color:'#fff', padding: isMobile ? '8px 10px' : '8px 14px', border:'none', borderRadius:'4px', cursor: hasSelectedDirections()?'pointer':'not-allowed', display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', fontWeight:600, opacity: hasSelectedDirections()?1:0.5 }}>
                    {isMobile ? <><Edit3 size={14} /><Save size={14} /></> : <><Edit3 size={14} /> Edit / <Save size={14} /> Save</>}
                  </button>
                  <button onClick={quickDownloadPDF} disabled={!hasSelectedDirections()||isGenerating} style={{ background: hasSelectedDirections()?'#00857E':'#555', color:'#fff', padding: isMobile ? '8px 10px' : '8px 14px', border:'none', borderRadius:'4px', cursor: hasSelectedDirections()&&!isGenerating?'pointer':'not-allowed', display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', fontWeight:600, opacity: hasSelectedDirections()&&!isGenerating?1:0.5 }}>
                    {isGenerating ? (isMobile ? <span className="spinner"/> : <><span className="spinner"/> Generating...</>) : (isMobile ? <Download size={14}/> : <><Download size={14}/> Download PDF</>)}
                  </button>
                </>
              ) : (
                <>
                  {savedMsg && !isMobile && <span style={{ fontSize:'0.76rem', color:'#fff', fontWeight:500 }}>✓ {savedMsg}</span>}
                  <button onClick={() => { setViewMode('selection'); }} style={{ background:'#6c757d', color:'#fff', padding: isMobile ? '8px 10px' : '8px 14px', border:'none', borderRadius:'4px', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', fontWeight:600 }}>
                    {isMobile ? <Eye size={14}/> : <><Eye size={14}/> Back to Selection</>}
                  </button>
                  <button onClick={() => downloadReportAsPDF(editableReportData)} disabled={isGenerating} style={{ background: isGenerating?'#555':'#00857E', color:'#fff', padding: isMobile ? '8px 10px' : '8px 14px', border:'none', borderRadius:'4px', cursor: isGenerating?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', fontWeight:600, opacity: isGenerating?0.5:1 }}>
                    {isGenerating ? (isMobile ? <span className="spinner"/> : <><span className="spinner"/> Generating...</>) : (isMobile ? <Download size={14}/> : <><Download size={14}/> Download PDF</>)}
                  </button>
                  <button onClick={captureAndSendReport} style={{ background:'#28a745', color:'#fff', padding: isMobile ? '8px 10px' : '8px 14px', border:'none', borderRadius:'4px', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', fontWeight:600 }}>
                    {isMobile ? <Save size={14}/> : <><Save size={14}/> Save Report</>}
                  </button>
                  <button onClick={leave.requestClose} style={{ background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.2)', color:'#fff', borderRadius:'4px', padding:'8px 10px', cursor:'pointer' }}><X size={15}/></button>
                </>
              )}
            </div>
          </div>

          {/* ── Body ── */}
          <div style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', flex:1, overflow:'hidden', height:'calc(100vh - 56px)' }}>
            {viewMode === 'selection' ? (
              <>
                {/* Left — entrance selector */}
                <div className="entrance-scroll-panel" style={{ width: isMobile ? '100%' : '50%', flex: isMobile ? (showMobilePlan ? '1 1 50%' : '1 1 100%') : undefined, background:'#C7F2F1', display:'flex', flexDirection:'column', borderRight: isMobile ? 'none' : '1px solid #333', borderBottom: isMobile ? '1px solid #333' : undefined, overflow:'hidden' }}>
                  <div style={{ padding:'12px 16px', background:'#00A19D', borderBottom:'1px solid #222', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0, flexWrap: isMobile ? 'wrap' : 'nowrap', gap:'8px' }}>
                    <span style={{ color:'#fff', fontSize:'13px', fontWeight:600 }}>Number of Entrances</span>
                    <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                      <button onClick={() => handleEntranceCountChange(-1)} disabled={entranceCount<=1} style={{ background:'#00A19D', color:'#fff', border:'none', borderRadius:'4px', width:'28px', height:'28px', cursor: entranceCount<=1?'not-allowed':'pointer', fontSize:'16px' }}>−</button>
                      <span style={{ color:'#fff', fontWeight:'bold', fontSize:'16px', minWidth:'24px', textAlign:'center' }}>{entranceCount}</span>
                      <button onClick={() => handleEntranceCountChange(1)} disabled={entranceCount>=20} style={{ background:'#00A19D', color:'#fff', border:'none', borderRadius:'4px', width:'28px', height:'28px', cursor: entranceCount>=20?'not-allowed':'pointer', fontSize:'16px' }}>+</button>
                      {/* Mobile only: collapse the canvas to give this
                          selector panel the full screen while filling in
                          entrance names/directions, per explicit request. */}
                      {isMobile && (
                        <button onClick={() => setShowMobilePlan(v => !v)} title={showMobilePlan ? 'Hide Plan' : 'Show Plan'}
                          style={{ background: showMobilePlan ? 'rgba(255,255,255,0.18)' : '#fff', color: showMobilePlan ? '#fff' : '#00857E', border:'1px solid #fff', borderRadius:'6px', padding:'5px 10px', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px', fontSize:'11px', fontWeight:700 }}>
                          {showMobilePlan ? <EyeOff size={13}/> : <Eye size={13}/>} Plan
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ flex:1, overflowY:'auto', overflowX:'hidden', padding:'12px', display:'flex', flexDirection:'column', gap:'10px' }}>
                    {Array.from({ length: entranceCount }, (_, i) => {
                      const key = `entrance_${i+1}`;
                      const selDirs = entranceDirections[key] || [];
                      const isExp = expandedEntrances[key];
                      return (
                        <div key={key} style={{ background:'#a3c9c9', borderRadius:'6px', border: selDirs.length>0?'1px solid #00857E':'1px solid #333', flexShrink:0 }}>
                          <div onClick={() => toggleExpand(key)} style={{ padding:'10px 12px', background:'#00A19D', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom: isExp?'1px solid #333':'none' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', flex:1 }}>
                              <DoorOpen size={14} color="#fff"/>
                              <input type="text" value={getEntranceName(key,`Entrance ${i+1}`)} onChange={e=>handleEntranceNameChange(key,e.target.value)} onClick={e=>e.stopPropagation()} placeholder={`Entrance ${i+1}`}
                                style={{ background:'#00A19D', border:'1px solid #fff', borderRadius:'4px', padding:'5px 8px', color:'#fff', fontSize:'12px', fontWeight:600, outline:'none', minWidth:'120px' }} />
                              {selDirs.length>0 && <span style={{ color:'#00857E', fontSize:'10px', background:'#fff', padding:'2px 8px', borderRadius:'10px', fontWeight:600 }}>{selDirs.length}</span>}
                            </div>
                            {isExp ? <ChevronUp size={14} color="#fff"/> : <ChevronDown size={14} color="#666"/>}
                          </div>
                          {isExp && (
                            <div style={{ padding:'12px', maxHeight:'none', overflow:'visible' }}>
                              <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(4,1fr)' : 'repeat(8,1fr)', gap:'4px' }}>
                                {entranceTypeDir.map(dir => {
                                  const isSel = selDirs.includes(dir);
                                  return (
                                    <div key={dir} onClick={() => handleDirectionToggle(key,dir)} style={{ background: isSel?'#00857E':'#fff', border: isSel?'2px solid #00857E':'1px solid #444', borderRadius:'4px', padding:'6px 2px', cursor:'pointer', fontSize:'10px', color: isSel?'#fff':'#00857E', textAlign:'center', fontWeight: isSel?700:500, userSelect:'none', minHeight:'28px', display:'flex', alignItems:'center', justifyContent:'center' }}>{dir}</div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Right — canvas (mobile: collapsible via the "Plan" toggle above) */}
                {(!isMobile || showMobilePlan) && (
                <div style={{ width: isMobile ? '100%' : '50%', flex: isMobile ? '1 1 50%' : undefined, background:'#111', display:'flex', flexDirection:'column', position:'relative' }}>
                  <div style={{ position:'absolute', top:'10px', right:'10px', zIndex:100, display:'flex', gap:'8px', background:'rgba(0,0,0,0.7)', padding:'8px', borderRadius:'6px', border:'1px solid #00857E' }}>
                    <button onClick={handleZoomOut} disabled={zoomLevel<=0.5} style={{ background: zoomLevel<=0.5?'#333':'#00857E', color: zoomLevel<=0.5?'#666':'#fff', border:'none', borderRadius:'4px', padding:'6px 10px', cursor: zoomLevel<=0.5?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:'4px', fontSize:'11px', fontWeight:600 }}><ZoomOut size={14}/></button>
                    <button onClick={handleResetZoom} style={{ background:'#444', color:'#fff', border:'1px solid #00857E', borderRadius:'4px', padding:'6px 10px', cursor:'pointer', display:'flex', alignItems:'center', gap:'4px', fontSize:'11px', fontWeight:600 }}><Maximize2 size={14}/>{Math.round(zoomLevel*100)}%</button>
                    <button onClick={handleZoomIn} disabled={zoomLevel>=3} style={{ background: zoomLevel>=3?'#333':'#00857E', color: zoomLevel>=3?'#666':'#fff', border:'none', borderRadius:'4px', padding:'6px 10px', cursor: zoomLevel>=3?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:'4px', fontSize:'11px', fontWeight:600 }}><ZoomIn size={14}/></button>
                  </div>
                  <div ref={canvasContainerRef} className="canvas-scroll-container" style={{ flex:1, overflow:'auto', display:'flex', alignItems: zoomLevel===1?'center':'flex-start', justifyContent: zoomLevel===1?'center':'flex-start', padding:'10px', background:'#ffffff' }}>
                    <canvas ref={canvasRef} style={{ display:'block', borderRadius:'4px' }} />
                  </div>
                </div>
                )}
              </>
            ) : (
              /* ── Edit view — rich text cards ── */
              <div className="entr-scroll" style={{ flex:1, background:'#f1f5f9', overflowY:'auto', padding: isMobile ? '14px 14px 30px' : '20px 24px 40px', fontFamily:"'Inter','Segoe UI',system-ui,sans-serif" }}>
                {groupedItems.map((group, gi) => (
                  <div key={gi} style={{ marginBottom:28 }}>
                    {/* Entrance group header */}
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                      <div style={{ width:32, height:32, borderRadius:8, background:'#00A19D', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.8rem', fontWeight:700, flexShrink:0 }}>{gi+1}</div>
                      <div style={{ fontSize:'1rem', fontWeight:700, color:'#0f172a' }}>{group.entranceName}</div>
                      <span style={{ fontSize:'0.72rem', background:'rgba(0,161,157,.15)', color:'#00857E', border:'1px solid rgba(0,161,157,.3)', borderRadius:20, padding:'2px 10px', fontWeight:600 }}>{group.items.length} direction{group.items.length>1?'s':''}</span>
                    </div>
                    {/* Direction cards */}
                    <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill,minmax(520px,1fr))', gap:14 }}>
                      {group.items.map(item => (
                        <EntranceCard key={item.id} item={item} onHtmlChange={html => handleHtml(item.id, html)} language={language} />
                      ))}
                    </div>
                  </div>
                ))}
                {groupedItems.length === 0 && (
                  <div style={{ padding:'60px 20px', color:'#94a3b8', textAlign:'center' }}>
                    <div style={{ fontSize:'2rem', marginBottom:10 }}>🚪</div>
                    <div style={{ fontSize:'0.88rem', fontWeight:500 }}>No entrance data available</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
